/**
 * The right-rail live preview. Renders every logical section (heading + body) on
 * a "paper" surface via the shared DocContractView, resolving binding tokens and
 * deferred blocks with the sample data - or, in placeholder mode, labelling them.
 * Selecting a section in the accordion scrolls it into view and highlights it here.
 */

import { DropdownMenu, PillRadio } from '@protoss/ui';
import * as React from 'react';
import { DocContractView, type DocHeading, type PreviewMode, type PreviewSample } from '../../../../components/doc';
import { sectionLabel, type LogicalSection } from '../../lib/sowContract';

export interface SowPreviewPaneProps {
  sections: LogicalSection[];
  sample: PreviewSample;
  headings: DocHeading[];
  selectedId: string | null;
  mode: PreviewMode;
  isEditing: boolean;
  onModeChange: (mode: PreviewMode) => void;
  onSelect: (id: string) => void;
  onEditSample: () => void;
}

export function SowPreviewPane(props: SowPreviewPaneProps) {
  const { sections, sample, headings, selectedId, mode, isEditing } = props;
  const pageRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!selectedId || !pageRef.current) return;
    const el = pageRef.current.querySelector<HTMLElement>(`[data-sow-section-id="${selectedId}"]`);
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
  }, [selectedId]);

  return (
    <div className="ptp-sow-preview">
      <div className="ptp-sow-preview__bar">
        <span className="ptp-sow-preview__title">Preview</span>
        <PillRadio
          options={[
            { value: 'sample', label: 'Sample data' },
            { value: 'placeholder', label: 'Placeholders' },
          ]}
          selectedValue={mode}
          onSelectionChange={(v) => v && props.onModeChange(v as PreviewMode)}
        />
        <span className="ptp-sow-preview__bar-spacer" />
        {isEditing ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button type="button" className="ptp-sow-preview__menu" aria-label="Preview options">
                ⋯
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end" className="dropdown-content">
              <DropdownMenu.Item className="dropdown-item" onSelect={props.onEditSample}>
                Edit mock data…
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ) : null}
      </div>

      <div className="ptp-sow-preview__page" ref={pageRef}>
        {sections.length === 0 ? (
          <p className="ptp-sow-preview__empty">Nothing to preview yet.</p>
        ) : (
          sections.map((section) => (
            <section
              key={section.id}
              data-sow-section-id={section.id}
              className={`ptp-sow-preview__sec${
                selectedId === section.id ? ' ptp-sow-preview__sec--active' : ''
              }`}
              onClick={() => props.onSelect(section.id)}
              aria-label={sectionLabel(section)}
            >
              <DocContractView
                content={section.heading ? [section.heading, ...section.body] : section.body}
                sample={sample}
                mode={mode}
                headings={headings}
              />
            </section>
          ))
        )}
      </div>
    </div>
  );
}
